import { resolve } from '../object/pathResolver';

let serviceDownErrorEventName = 'service_down_error';

export const setServiceDownErrorEventName = name => {
  if (typeof name === 'string' && name.trim()) {
    serviceDownErrorEventName = name.trim();
  }
};

const pattern = new RegExp(/(?<={)([a-zA-Z0-9\.][^}]+)/g);

export const resolveEvent = (event, obj) => {
  try {
    let resolvedEvent = event;
    if (event.includes('{')) {
      const tokens = event.match(pattern);
      tokens.forEach(token => {
        const value = resolve(token, obj);
        if (value) {
          resolvedEvent = resolvedEvent.replace(`{${token}}`, value);
        }
      });
    }
    return JSON.parse(resolvedEvent);
  } catch {
    return {
      event: 'Event resolve failure',
      content: event,
    };
  }
};

export const pushEvent = event => {
  try {
    if (typeof window === 'undefined' || !event) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  } catch {
    // Do nothing - dataLayer failure should not stop JS execution
  }
};

const extractEndpointPath = endpoint => {
  if (!endpoint) {
    return '';
  }
  try {
    const base =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : 'http://localhost';
    const url = new URL(endpoint, base);
    return url.pathname;
  } catch {
    return typeof endpoint === 'string' ? endpoint.split('?')[0] : '';
  }
};

const MESSAGE_KEYS = ['message', 'error', 'errorMessage', 'title'];

const pickMessageFromData = data => {
  if (!data) {
    return undefined;
  }
  if (typeof data === 'string') {
    return data;
  }
  const key = MESSAGE_KEYS.find(k => data[k]);
  if (key) {
    return data[key];
  }
  if (Array.isArray(data.errors)) {
    return data.errors.join('; ');
  }
  return JSON.stringify(data);
};

const extractJsonErrorMessage = cloned =>
  cloned
    .json()
    .then(pickMessageFromData)
    .catch(() => undefined);

const extractTextErrorMessage = cloned =>
  cloned
    .text()
    .then(text => text || undefined)
    .catch(() => undefined);

const extractErrorMessage = response => {
  if (!response || typeof response.clone !== 'function') {
    return Promise.resolve(undefined);
  }
  try {
    const cloned = response.clone();
    const contentType = cloned.headers?.get('Content-Type') || '';
    return contentType.includes('application/json')
      ? extractJsonErrorMessage(cloned)
      : extractTextErrorMessage(cloned);
  } catch {
    return Promise.resolve(undefined);
  }
};

const getErrorMessage = error => {
  if (!error) {
    return undefined;
  }
  return typeof error === 'string' ? error : error?.message;
};

/**
 * Push a `service_down_error` event to the GTM dataLayer when an API
 * service call has failed (non-OK response, network error or thrown error).
 */
export const pushServiceDownError = (endpoint, response, error) => {
  const event = {
    event: serviceDownErrorEventName,
    endpoint: extractEndpointPath(endpoint),
  };

  const messagePromise =
    response && typeof response.status === 'number'
      ? (() => {
          event.http_status_code = response.status;
          return extractErrorMessage(response);
        })()
      : Promise.resolve(getErrorMessage(error));

  return messagePromise
    .then(message => {
      if (message) {
        event.error_message = message;
      }
      pushEvent(event);
    })
    .catch(() => {
      // Do nothing - tracking failure should not affect application flow
    });
};
